import {
  CorporateFare as CorporateFareIcon,
  AllInbox as AllInboxIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  ViewKanban as ViewKanbanIcon,
  Speed as SpeedIcon,
  ManageSearch as ManageSearchIcon,
  Schema as SchemaIcon,
  SmartToy as SmartToyIcon,
  FormatListBulletedOutlined,
  SpaceDashboardOutlined,
} from '@mui/icons-material';
import { canAccessBids } from '../../utils/bidsAccess';

/**
 * The one nav config (ticket #295) for the desktop sidebar and the mobile menu.
 *
 * Each group declares the workspace(s) it belongs to ('project', 'bid'); each
 * item has an access(auth) check. Groups render in this order, items in their
 * declared order, with a divider between non-empty groups. The project groups
 * reproduce the pre-#295 sidebar exactly, legacy title-case labels included.
 *
 * A future shared item (e.g. Assistant) goes in a group with
 * workspaces: ['project', 'bid'] and gives one path per workspace:
 *   { id: 'assistant', text: 'Assistant', icon: …,
 *     paths: { project: '/assistant', bid: '/bids/assistant' }, access: … }
 */
export const NAV_GROUPS = [
  {
    id: 'admin',
    workspaces: ['project'],
    items: [
      {
        id: 'projects',
        text: 'Projects',
        icon: CorporateFareIcon,
        path: '/projects',
        access: (a) => !!a.isAdmin,
      },
      {
        id: 'packages',
        text: 'Packages',
        icon: AllInboxIcon,
        path: '/packages',
        access: (a) => !!a.isAdmin,
      },
      {
        id: 'employees',
        text: 'Employees',
        icon: GroupIcon,
        path: '/employees',
        access: (a) => !!a.isAdmin,
      },
      {
        id: 'workflowTemplate',
        text: 'Workflow Template',
        icon: SchemaIcon,
        path: '/workflow-template',
        access: (a) => !!a.isAdmin && !!a.isSuperAdmin,
      },
    ],
  },
  {
    id: 'timecards',
    workspaces: ['project'],
    items: [
      {
        id: 'timecards',
        text: 'Timecards',
        icon: AccessTimeIcon,
        path: '/timecards',
        access: (a) => !!(a.isAdmin || a.isTimecard),
      },
      {
        id: 'timeApproval',
        text: 'Time Approval',
        icon: DescriptionIcon,
        path: '/reports',
        access: (a) => !!(a.isAdmin || a.isTimecard),
      },
    ],
  },
  {
    id: 'features',
    workspaces: ['project'],
    items: [
      {
        id: 'workflow',
        text: 'Workflow',
        icon: ViewKanbanIcon,
        path: '/package-workflow',
        access: (a) => !!(a.isAdmin || a.isWorkflow),
      },
      {
        id: 'laborAnalytics',
        text: 'Labor Analytics',
        icon: ManageSearchIcon,
        path: '/analytics',
        access: (a) => !!(a.isAdmin || a.isAnalytics),
      },
      {
        id: 'productionMetrics',
        text: 'Production Metrics',
        icon: SpeedIcon,
        path: '/analytics/production-metrics',
        access: (a) => !!(a.isAdmin || a.isAnalytics),
      },
    ],
  },
  {
    // Project Documents Assistant beta (#250): super-admins only, and only once
    // the server has confirmed the feature is on for this session — the same
    // condition as the App.js route. Not tied to isAdmin.
    id: 'assistant',
    workspaces: ['project'],
    items: [
      {
        id: 'projectAssistant',
        text: 'Project Assistant',
        icon: SmartToyIcon,
        path: '/project-documents-assistant',
        access: (a) => !!a.isSuperAdmin && !!a.projectDocumentsEnabled,
      },
    ],
  },
  {
    id: 'bids',
    workspaces: ['bid'],
    items: [
      {
        id: 'allBids',
        text: 'All bids',
        icon: FormatListBulletedOutlined,
        path: '/bids',
        access: canAccessBids,
      },
      {
        id: 'bidOverview',
        text: 'Overview',
        icon: SpaceDashboardOutlined,
        path: '/bids/overview',
        access: canAccessBids,
      },
    ],
  },
];

/** An item's path in a workspace: shared items give `paths`, others `path`. */
export const navItemPath = (item, workspaceType) =>
  item.paths ? item.paths[workspaceType] : item.path;

/**
 * The groups to render for a workspace and user: groups that include
 * workspaceType, items whose access(auth) passes, empty groups dropped.
 * Order is exactly as declared. Pass `groups` to test with your own config.
 *
 * @param {'project'|'bid'} workspaceType
 * @param {object} auth - the auth context values
 * @returns {Array<{ id, items }>}
 */
export function getNavGroups(workspaceType, auth, groups = NAV_GROUPS) {
  return groups
    .filter((group) => group.workspaces.includes(workspaceType))
    .map((group) => ({
      id: group.id,
      items: group.items.filter((item) => item.access(auth || {})),
    }))
    .filter((group) => group.items.length > 0);
}
